/**
 * Deepgram Flux STT — WebSocket client for real-time conversational speech recognition.
 * Uses Flux on v2/listen with native turn detection (EndOfTurn).
 * Falls back to Nova-3 on v1/listen if Flux connection fails.
 * Auth via WebSocket subprotocol ['token', key] (browser-compatible).
 */
import { z } from 'zod';

export interface FluxCallbacks {
  onTranscript?: (text: string, isFinal: boolean) => void;
  onEndOfTurn?: (transcript: string) => void;
  onStartOfTurn?: () => void;
  onError?: (error: Error) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

/** Flux v2 TurnInfo response */
interface FluxTurnInfoMessage {
  type: 'TurnInfo';
  event: 'Update' | 'StartOfTurn' | 'EndOfTurn';
  turn_index: number;
  transcript: string;
  words: Array<{ word: string; confidence: number; start: number; end: number }>;
  end_of_turn_confidence: number;
  sequence_id: number;
  request_id: string;
}

/** Nova-3 v1 Results response (fallback) */
interface NovaResultMessage {
  type: 'Results';
  is_final: boolean;
  speech_final: boolean;
  channel: {
    alternatives: Array<{
      transcript: string;
      confidence: number;
    }>;
  };
}

type DeepgramMessage = FluxTurnInfoMessage | NovaResultMessage | { type: string; [key: string]: unknown };

const FLUX_WS_URL = 'wss://api.deepgram.com/v2/listen';
const NOVA_WS_URL = 'wss://api.deepgram.com/v1/listen';
const KEEPALIVE_INTERVAL_MS = 5000;

export class DeepgramFluxSTT {
  private ws: WebSocket | null = null;
  private keepaliveTimer: ReturnType<typeof setInterval> | null = null;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private callbacks: FluxCallbacks = {};
  private accumulatedTranscript = '';
  private connected = false;
  private speechActive = false;
  private useFlux = true;

  // Silence timeout: if we have text but no speech_final/UtteranceEnd after Nms, auto-send.
  // Tunable via VITE_VOICE_SILENCE_MS (default 2000).
  private static SILENCE_TIMEOUT_MS = Number(
    import.meta.env.VITE_VOICE_SILENCE_MS ?? 2000
  );

  setCallbacks(cbs: FluxCallbacks): void {
    this.callbacks = cbs;
  }

  async connect(token: string, sampleRate: number = 16000): Promise<void> {
    if (this.ws) {
      this.disconnect();
    }

    this.accumulatedTranscript = '';
    this.connected = false;
    this.speechActive = false;

    // Use Nova-3 v1 — proven working, has speech_final + utterance_end for turn detection
    // TODO: Re-enable Flux v2 once empty transcript issue is resolved
    await this.connectToEndpoint(token, false, sampleRate);
    this.useFlux = false;
  }

  private async connectToEndpoint(token: string, flux: boolean, sampleRate: number): Promise<void> {
    const params = new URLSearchParams({
      model: flux ? 'flux-general-en' : 'nova-3',
      encoding: 'linear16',
      sample_rate: String(sampleRate),
    });

    if (!flux) {
      params.set('channels', '1');
      params.set('interim_results', 'true');
      // Deepgram endpointing (ms of silence before speech_final) + utterance_end_ms.
      // Both tunable via VITE_VOICE_SILENCE_MS so Deepgram waits the full window
      // before firing end-of-turn, instead of cutting off at ~10ms default.
      const silenceMs = DeepgramFluxSTT.SILENCE_TIMEOUT_MS;
      params.set('endpointing', String(silenceMs));
      params.set('utterance_end_ms', String(Math.max(silenceMs, 1000)));
      params.set('vad_events', 'true');
      params.set('smart_format', 'true');
    }

    const baseUrl = flux ? FLUX_WS_URL : NOVA_WS_URL;
    const url = `${baseUrl}?${params.toString()}`;

    return new Promise<void>((resolve, reject) => {
      try {
        this.ws = new WebSocket(url, ['token', token]);
        this.ws.binaryType = 'arraybuffer';
      } catch (err) {
        reject(new Error(`Failed to create WebSocket: ${err}`));
        return;
      }

      const timeout = setTimeout(() => {
        if (!this.connected) {
          this.disconnect();
          reject(new Error('Deepgram connection timeout'));
        }
      }, 10000);

      this.ws.onopen = () => {
        clearTimeout(timeout);
        this.connected = true;
        this.startKeepalive();
        console.log(`[DeepgramSTT] Connected to ${flux ? 'Flux v2' : 'Nova-3 v1'}`);
        this.callbacks.onOpen?.();
        resolve();
      };

      this.ws.onmessage = (event: MessageEvent) => {
        this.handleMessage(event.data);
      };

      this.ws.onerror = () => {
        clearTimeout(timeout);
        const error = new Error('Deepgram WebSocket error');
        if (!this.connected) {
          reject(error);
        } else {
          this.callbacks.onError?.(error);
        }
      };

      this.ws.onclose = () => {
        clearTimeout(timeout);
        const wasConnected = this.connected;
        this.connected = false;
        this.stopKeepalive();
        this.callbacks.onClose?.();
        if (!wasConnected) {
          reject(new Error('Deepgram WebSocket closed before connecting'));
        }
      };
    });
  }

  disconnect(): void {
    this.stopKeepalive();
    this.clearSilenceTimer();

    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ type: 'CloseStream' }));
        } catch {
          // Ignore send errors during close
        }
      }
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }

    this.connected = false;
    this.accumulatedTranscript = '';
    this.speechActive = false;
  }

  private toArrayBuffer(view: ArrayBufferView): ArrayBuffer {
    const bytes = new Uint8Array(view.byteLength);
    bytes.set(new Uint8Array(view.buffer, view.byteOffset, view.byteLength));
    return bytes.buffer;
  }

  sendAudio(data: ArrayBuffer | Int16Array | Float32Array): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    let buffer: ArrayBuffer;
    if (data instanceof Float32Array) {
      const int16 = new Int16Array(data.length);
      for (let i = 0; i < data.length; i++) {
        const s = Math.max(-1, Math.min(1, data[i]));
        int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
      }
      buffer = this.toArrayBuffer(int16);
    } else if (data instanceof Int16Array) {
      buffer = this.toArrayBuffer(data);
    } else {
      buffer = data;
    }

    this.ws.send(buffer);
  }

  isConnected(): boolean {
    return this.connected && this.ws?.readyState === WebSocket.OPEN;
  }

  getAccumulatedTranscript(): string {
    return this.accumulatedTranscript;
  }

  private handleMessage(data: unknown): void {
    if (typeof data !== 'string') return;

    let msg: DeepgramMessage;
    try {
      const parsed: unknown = JSON.parse(data);
      const result = z.object({ type: z.string() }).passthrough().safeParse(parsed);
      if (!result.success) return;
      msg = result.data;
    } catch {
      return;
    }

    // Log all messages for debugging
    console.log('[DeepgramSTT] Message:', msg.type, this.useFlux ? '(Flux)' : '(Nova)',
      JSON.stringify(msg).slice(0, 200));

    if (this.useFlux) {
      this.handleFluxMessage(msg);
    } else {
      this.handleNovaMessage(msg);
    }
  }

  /** Handle Flux v2 TurnInfo messages */
  private handleFluxMessage(msg: DeepgramMessage): void {
    if (msg.type !== 'TurnInfo') return;

    const turnInfo = msg as FluxTurnInfoMessage;

    switch (turnInfo.event) {
      case 'StartOfTurn':
        this.accumulatedTranscript = '';
        this.callbacks.onStartOfTurn?.();
        break;

      case 'Update':
        if (turnInfo.transcript) {
          this.accumulatedTranscript = turnInfo.transcript;
          this.callbacks.onTranscript?.(turnInfo.transcript, false);
        }
        break;

      case 'EndOfTurn':
        if (turnInfo.transcript) {
          this.accumulatedTranscript = turnInfo.transcript;
        }
        this.callbacks.onTranscript?.(this.accumulatedTranscript, true);
        this.callbacks.onEndOfTurn?.(this.accumulatedTranscript);
        break;
    }
  }

  /** Handle Nova-3 v1 Results messages (fallback) */
  private handleNovaMessage(msg: DeepgramMessage): void {
    if (msg.type === 'SpeechStarted') {
      if (!this.speechActive) {
        this.speechActive = true;
        this.callbacks.onStartOfTurn?.();
      }
      this.clearSilenceTimer();
      return;
    }

    if (msg.type === 'UtteranceEnd') {
      this.clearSilenceTimer();
      if (this.accumulatedTranscript.trim()) {
        console.log('[DeepgramSTT] UtteranceEnd — sending:', this.accumulatedTranscript.trim());
        this.callbacks.onEndOfTurn?.(this.accumulatedTranscript.trim());
        this.accumulatedTranscript = '';
      }
      this.speechActive = false;
      return;
    }

    if (msg.type !== 'Results') return;

    const result = msg as NovaResultMessage;
    const transcript = result.channel?.alternatives?.[0]?.transcript || '';
    if (!transcript) return;

    if (result.is_final) {
      this.accumulatedTranscript += (this.accumulatedTranscript ? ' ' : '') + transcript;
      this.callbacks.onTranscript?.(this.accumulatedTranscript, true);

      if (result.speech_final) {
        this.clearSilenceTimer();
        console.log('[DeepgramSTT] speech_final — sending:', this.accumulatedTranscript.trim());
        this.callbacks.onEndOfTurn?.(this.accumulatedTranscript.trim());
        this.accumulatedTranscript = '';
        this.speechActive = false;
      } else {
        // Got a final segment but not speech_final — start silence timer as safety net
        this.resetSilenceTimer();
      }
    } else {
      const interimFull = this.accumulatedTranscript
        ? this.accumulatedTranscript + ' ' + transcript
        : transcript;
      this.callbacks.onTranscript?.(interimFull, false);
      // Reset silence timer on any interim result (user is still talking)
      this.resetSilenceTimer();
    }
  }

  private resetSilenceTimer(): void {
    this.clearSilenceTimer();
    if (this.accumulatedTranscript.trim()) {
      this.silenceTimer = setTimeout(() => {
        if (this.accumulatedTranscript.trim()) {
          console.log('[DeepgramSTT] Silence timeout — sending:', this.accumulatedTranscript.trim());
          this.callbacks.onEndOfTurn?.(this.accumulatedTranscript.trim());
          this.accumulatedTranscript = '';
          this.speechActive = false;
        }
      }, DeepgramFluxSTT.SILENCE_TIMEOUT_MS);
    }
  }

  private clearSilenceTimer(): void {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
  }

  private startKeepalive(): void {
    this.stopKeepalive();
    // Flux v2 only accepts CloseStream and Configure — no KeepAlive.
    // Nova v1 accepts KeepAlive.
    if (this.useFlux) return; // Flux doesn't need keepalive — audio flow keeps connection alive

    this.keepaliveTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'KeepAlive' }));
      }
    }, KEEPALIVE_INTERVAL_MS);
  }

  private stopKeepalive(): void {
    if (this.keepaliveTimer) {
      clearInterval(this.keepaliveTimer);
      this.keepaliveTimer = null;
    }
  }
}
